import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { BulkUpdateSelection } from '~/api/civilsearch';
import { Notify } from '~/components2/Notify';
import { useBulkCreateDataRequestNotes } from '~/hooks/civilsearch/useDataRequest';

export type UseBulkEditNotesParams = {
  buildSearchPayloadForBulk: () => { [key: string]: any };
  getActiveQueryKeyParams: () => object;
};

export function useBulkEditNotes({ buildSearchPayloadForBulk, getActiveQueryKeyParams }: UseBulkEditNotesParams) {
  const queryClient = useQueryClient();
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [notes, setNotes] = useState<string>('');
  const mutation = useBulkCreateDataRequestNotes();

  const open = useCallback((selection: ServerSideSelectionState) => {
    setPendingSelection(selection);
    setNotes('');
  }, []);

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const onSubmit = useCallback(() => {
    if (!pendingSelection) return;

    const selection: BulkUpdateSelection =
      pendingSelection.mode === 'all' ? { ...pendingSelection, search: buildSearchPayloadForBulk() } : pendingSelection;

    const activeQueryKey = ['data-requests-v2', getActiveQueryKeyParams()];

    mutation.mutate(
      { selection, note: notes },
      {
        onSuccess: () => {
          setPendingSelection(null);
          queryClient.invalidateQueries({ queryKey: activeQueryKey });
        },
        onError: () => {
          Notify.error({ message: 'Failed to add notes. Please try again.' });
        },
      },
    );
  }, [pendingSelection, notes, buildSearchPayloadForBulk, getActiveQueryKeyParams, mutation, queryClient]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    notes,
    setNotes,
    onSubmit,
    isPending: mutation.isPending,
  };
}
